import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import axios from 'axios';
import { asList } from '../../../../utils/apiList';
import { useAuth } from '../../../../contexts/AuthContext';


import { API_URL } from '../../../../config';
interface OMRSubmission {
  id: number;
  student_id: number;
  exam_omr_template_id: number;
  image_path: string;
  processed_image_path: string;
  answers_json: string;
  score: number;
  max_score: number;
  status: string;
  error_message: string | null;
  processed_at: string;
  created_at: string;
  updated_at: string;
  student?: {
    email: string;
    username: string;
    role: string;
    id: number;
    full_name: string;
    created_at: string;
    updated_at: string;
    last_login: string;
  };
  exam_omr_template?: {
    exam_id: number;
    omr_template_id: number;
    answers_json: string;
    id: number;
    created_at: string;
    updated_at: string | null;
    omr_template: {
      name: string;
      description: string;
      num_questions: number;
      config: any;
      id: number;
      file_path: string;
      created_by: number;
      created_at: string;
      updated_at: string | null;
    };
    exam: {
      title: string;
      description: string;
      start_datetime: string;
      end_datetime: string;
      duration_minutes: number;
      max_marks: number;
      max_questions: number;
      course_id: number;
      class_id: number | null;
      subject_id: number | null;
      chapter_id: number | null;
      topic_id: number | null;
      id: number;
      created_by: number;
      created_at: string;
      updated_at: string | null;
      creator: {
        email: string;
        username: string;
        role: string;
        id: number;
        full_name: string;
        created_at: string;
        updated_at: string;
        last_login: string;
      };
    };
  };
}

interface SubmissionResult {
  student_answers: Record<string, string>;
  correct_answers: Record<string, string>;
  score: number;
  max_score: number;
  total_questions: number;
  attempted: number;
  correct: number;
  score_percentage: number;
  question_details: Array<{
    question_number: string;
    correct_option: string;
    student_option: string;
    is_correct: boolean;
  }>;
  debug_image: string | null;
}

const AdminOMRSubmissionList: React.FC = () => {
  const [submissions, setSubmissions] = useState<OMRSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<OMRSubmission | null>(null);
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [examFilter, setExamFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const { token } = useAuth();

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/api/omr/omr/submissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSubmissions(asList(response.data));
    } catch (error: any) {
      console.error('Error fetching submissions:', error);
      setError(error.response?.data?.detail || 'Error fetching submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSubmissions();
    }
  }, [token]);

  const handleViewResult = async (submission: OMRSubmission) => {
    try {
      setSelectedSubmission(submission);
      setError(null);
      const response = await axios.get(
        `${API_URL}/api/omr/omr/submissions/${submission.id}/result`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setSubmissionResult(response.data);
      setOpenDialog(true);
    } catch (error: any) {
      console.error('Error fetching submission result:', error);
      setError(error.response?.data?.detail || 'Error fetching submission result');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'processed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="flex flex-col">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>OMR Submissions</h2>
        <p className="mt-1 text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>View and manage all student OMR submissions</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-600 ">Exam Name</label>
          <input
            type="text"
            value={examFilter}
            onChange={e => setExamFilter(e.target.value)}
            placeholder="Filter by exam name"
            className="border border-gray-300 rounded px-3 py-2 w-48 focus:outline-none focus:ring-2 focus:ring-gray-700"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 ">Submitted At</label>
          <input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-48 focus:outline-none focus:ring-2 focus:ring-gray-700"
          />
        </div>
        <div>
          <button
            type="button"
            onClick={() => { setExamFilter(''); setDateFilter(''); }}
            className="mt-5 bg-gray-200 hover:bg-gray-300 text-gray-600 font-medium py-2 px-4 rounded"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {error && (
        <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <CircularProgress />
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-8">
          <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Inter, sans-serif' }}>
            No submissions found
          </Typography>
        </div>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: '0.5rem' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Exam Title</TableCell>
                <TableCell sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Student Email</TableCell>
                <TableCell sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Submitted At</TableCell>
                <TableCell sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Processed At</TableCell>
                <TableCell align="center" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions
                .filter(sub =>
                  (!examFilter || sub.exam_omr_template?.exam?.title?.toLowerCase().includes(examFilter.toLowerCase())) &&
                  (!dateFilter || new Date(sub.created_at).toISOString().slice(0, 10) === dateFilter)
                )
                .length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ fontFamily: 'Inter, sans-serif' }}>
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  submissions
                    .filter(sub =>
                      (!examFilter || sub.exam_omr_template?.exam?.title?.toLowerCase().includes(examFilter.toLowerCase())) &&
                      (!dateFilter || new Date(sub.created_at).toISOString().slice(0, 10) === dateFilter)
                    )
                    .map((submission) => (
                      <TableRow key={submission.id}>
                        <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>{submission.exam_omr_template?.exam?.title || '-'}</TableCell>
                        <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>{submission.student?.email || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={submission.status}
                            color={getStatusColor(submission.status) as any}
                            size="small"
                            sx={{ fontFamily: 'Inter, sans-serif' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>{formatDate(submission.created_at)}</TableCell>
                        <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>
                          {submission.processed_at ? formatDate(submission.processed_at) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleViewResult(submission)}
                            className="text-[#111827] hover:text-blue-800"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '0.5rem',
            fontFamily: 'Inter, sans-serif'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid', 
          borderColor: 'divider', 
          pb: 2,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600
        }}>
          Submission Result
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {submissionResult && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Score Summary</Typography>
                    <div className="space-y-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      <div className="flex justify-between">
                        <span>Score:</span>
                        <span>{submissionResult.score} / {submissionResult.max_score}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Percentage:</span>
                        <span>{submissionResult.score_percentage}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Questions Attempted:</span>
                        <span>{submissionResult.attempted} / {submissionResult.total_questions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Correct Answers:</span>
                        <span>{submissionResult.correct}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Submission Details</Typography>
                    <div className="space-y-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      <div className="flex justify-between">
                        <span>Status:</span>
                        <Chip
                          label={selectedSubmission?.status}
                          color={getStatusColor(selectedSubmission?.status || '') as any}
                          size="small"
                          sx={{ fontFamily: 'Inter, sans-serif' }}
                        />
                      </div>
                      <div className="flex justify-between">
                        <span>Submitted:</span>
                        <span>{selectedSubmission && formatDate(selectedSubmission.created_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Processed:</span>
                        <span>
                          {selectedSubmission?.processed_at ? formatDate(selectedSubmission.processed_at) : '-'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Question Details</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Question</TableCell>
                          <TableCell sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Student Answer</TableCell>
                          <TableCell sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Correct Answer</TableCell>
                          <TableCell sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Result</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {submissionResult.question_details.map((detail) => (
                          <TableRow key={detail.question_number}>
                            <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>Question {detail.question_number}</TableCell>
                            <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>{detail.student_option || '-'}</TableCell>
                            <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>{detail.correct_option}</TableCell>
                            <TableCell>
                              <Chip
                                label={detail.is_correct ? "Correct" : "Incorrect"}
                                color={detail.is_correct ? "success" : "error"}
                                size="small"
                                sx={{ fontFamily: 'Inter, sans-serif' }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {submissionResult.debug_image && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Debug Image</Typography>
                    <img 
                      src={submissionResult.debug_image} 
                      alt="Debug visualization" 
                      className="w-full h-auto"
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOMRSubmissionList; 
